/**
 * The typed client over BEST's JSON API.
 *
 * Same origin, so the signed session cookie travels on its own and there is no
 * token to keep in the browser.  A 401 means the session has gone, and the
 * only useful response is to send the person back through GitLab rather than
 * to show them an error they cannot act on.
 */

import type {
  AttributeTypeChoice,
  BranchRow,
  ChildPartRow,
  CommitHistoryPayload,
  ConnectionEndpoint,
  CoverageAxis,
  CoverageGroup,
  CreatedPart,
  CsvImportResult,
  CurrentProject,
  DeleteImpact,
  DeleteResult,
  DesignRow,
  ElementRow,
  EntityRow,
  FeedbackTarget,
  Finding,
  FinishResult,
  FlowdownRow,
  GlossaryPage,
  ImportResult,
  InterfaceFeature,
  InterfaceTypeRow,
  FeatureDraft,
  Pinout,
  IssueRow,
  MembersPayload,
  ApprovalResult,
  BaselineArtifacts,
  BaselineCut,
  BaselineList,
  BaselineMainMoved,
  BaselineNotOpened,
  BaselineStatus,
  DiscussionList,
  ElementCommentResult,
  MergeRequest,
  MergeResult,
  MergeState,
  ModelChanges,
  PackageRow,
  Page,
  PartConnection,
  PartDetail,
  PartsPage,
  ProjectRow,
  RaisedIssue,
  RollingAttribute,
  Schema,
  SourceFileRow,
  StateRow,
  StateValue,
  Succession,
  LinkReport,
  StateGroupResult,
  StateMap,
  StateUsageCreated,
  TouchingTransition,
  TransitionEdit,
  LayoutBox,
  ViewDetail,
  Suggestion,
  StatusReport,
  StatusReportFilters,
  Summary,
  SysmlPasteResult,
  VerificationRow,
  WorkItem,
  RequirementTypeRow,
  VersionInfo,
} from './schema'

/**
 * The program every request is about.
 *
 * A module variable rather than an argument on each call, and set in ONE place
 * so it cannot be forgotten: there are some forty call sites below, and a
 * per-call parameter would eventually be omitted from one of them, which shows
 * up as a screen quietly reading another program's model.
 *
 * Empty means "let the server decide" - where this person left off, or the
 * only program there is. That is what the first load uses, before the
 * picker knows anything.
 */
let projectSlug = ''

/** Address every subsequent request to this program. */
export function setProjectSlug(slug: string): void {
  projectSlug = slug
}

/** The program requests are currently addressed to, or '' for the default. */
export function getProjectSlug(): string {
  return projectSlug
}

/** A refusal from the service layer, carrying what the back end actually said. */
export class ApiError extends Error {
  readonly status: number
  readonly kind: string
  /** Field-level complaints from the generated request model, if any. */
  readonly fields: Record<string, string>
  /**
   * What the server said had gone wrong UNDERNEATH its own message, when it
   * sent anything: the 503 for a model BEST cannot read carries the SQLite
   * error in `error`, and that is the only thing separating 'no such table'
   * from 'unable to open database file'. The message above is the same
   * sentence in both cases, which is why one of them cost a round trip to
   * diagnose.
   */
  readonly underlying: string

  constructor(
    status: number,
    message: string,
    kind = '',
    fields: Record<string, string> = {},
    underlying = '',
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.kind = kind
    this.fields = fields
    this.underlying = underlying
  }
}

/** Where the browser should go to sign in again, coming back to this page. */
export function signInUrl(): string {
  return `/auth/login?next=${encodeURIComponent(window.location.pathname)}`
}

/**
 * Told when the API has refused a call for want of a signed-in person.
 *
 * BEST answers an unauthenticated caller with 401 on every route that carries
 * model data, so a session that runs out mid-use would otherwise show up as
 * whichever panels happened to reload failing one after another. The shell
 * subscribes to this and puts the sign-in screen back up, which is the only
 * thing the person can actually do about it.
 *
 * Returns its own unsubscribe, so a caller in an effect can return it directly.
 */
type UnauthorizedHandler = () => void

const unauthorizedHandlers = new Set<UnauthorizedHandler>()

export function onUnauthorized(handler: UnauthorizedHandler): () => void {
  unauthorizedHandlers.add(handler)
  return () => {
    unauthorizedHandlers.delete(handler)
  }
}

function announceUnauthorized(): void {
  for (const handler of [...unauthorizedHandlers]) handler()
}

/**
 * The header the server adds to any response caused by GitLab not answering.
 *
 * Read off the failing call itself, so the shell learns GitLab is down when a
 * save fails and not only at its next poll.
 */
const GITLAB_UNREACHABLE_HEADER = 'X-BEST-GitLab-Unreachable'

const unreachableHandlers = new Set<() => void>()

/** Be told when a response says GitLab is not responding. */
export function onGitLabUnreachable(handler: () => void): () => void {
  unreachableHandlers.add(handler)
  return () => {
    unreachableHandlers.delete(handler)
  }
}

function announceIfUnreachable(response: Response): void {
  if (response.headers?.get(GITLAB_UNREACHABLE_HEADER)) {
    for (const handler of [...unreachableHandlers]) handler()
  }
}

/**
 * Pull a readable message, and any per-field complaints, out of a failure.
 *
 * FastAPI answers a refused write with {detail: "..."} and a rejected body
 * with {detail: [{loc, msg}, ...]}.  Both arrive as 4xx, so both have to be
 * understood here or the second shows up as "[object Object]".
 */
function describe(status: number, body: unknown): ApiError {
  if (typeof body !== 'object' || body === null) {
    return new ApiError(status, `Request failed (${status})`)
  }

  const payload = body as { detail?: unknown; kind?: string; error?: unknown }
  const detail = payload.detail
  const underlying = typeof payload.error === 'string' ? payload.error : ''

  if (typeof detail === 'string') {
    return new ApiError(status, detail, payload.kind ?? '', {}, underlying)
  }

  if (Array.isArray(detail)) {
    const fields: Record<string, string> = {}
    for (const entry of detail as Array<{ loc?: unknown[]; msg?: string }>) {
      // loc is ['body', '<field>'] for a body problem; the last segment is the
      // field name, and an entry without one is a whole-body complaint.
      const where = Array.isArray(entry.loc) ? String(entry.loc.at(-1) ?? '') : ''
      if (where && where !== 'body') fields[where] = entry.msg ?? 'is not valid'
    }
    const names = Object.keys(fields)
    const summary = names.length
      ? `${names.join(', ')}: ${fields[names[0]]}`
      : `Request failed (${status})`
    return new ApiError(status, summary, 'ValidationError', fields)
  }

  // A refused import answers {detail: {message, errors: [{path, detail}]}} —
  // an object rather than a string, because naming which files would not parse
  // is the whole value of the refusal.
  if (detail && typeof detail === 'object') {
    const structured = detail as {
      message?: string
      errors?: Array<{ path?: string; detail?: string }>
    }
    if (structured.message) {
      const fields: Record<string, string> = {}
      for (const entry of structured.errors ?? []) {
        if (entry.path) fields[entry.path] = entry.detail ?? ''
      }
      return new ApiError(status, structured.message, payload.kind ?? '', fields)
    }
  }

  return new ApiError(status, `Request failed (${status})`, payload.kind ?? '', {}, underlying)
}

/**
 * Turn a refused response into the error `request` throws, or return.
 *
 * Shared with the file downloads so a refused export reads exactly like a
 * refused anything-else - the same 401 handling (the shell puts sign-in back)
 * and the same `describe`d message - rather than a second copy of this that
 * would drift the first time one of them learned a new failure.
 */
async function ensureOk(response: Response): Promise<void> {
  if (response.status === 401) {
    announceUnauthorized()
    throw new ApiError(401, 'Your session has expired. Sign in again to continue.', 'Unauthorized')
  }

  if (!response.ok) {
    announceIfUnreachable(response)
    let body: unknown = null
    try {
      body = await response.json()
    } catch {
      // A proxy or a crash can answer with HTML; the status is still the fact.
    }
    throw describe(response.status, body)
  }
}

/**
 * One request, with the failure modes turned into something actionable.
 */
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(projectSlug ? { 'X-BEST-Project': projectSlug } : {}),
      ...init.headers,
    },
  })

  await ensureOk(response)

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

/**
 * POST a body and save the file that comes back.
 *
 * For the exports that depend on a body - the ids the table is showing - and so
 * cannot be a link the browser follows. The price of that: nothing downloads
 * until the whole response is in memory as a Blob, so this is for a few
 * thousand rows of CSV and not for a database dump.
 *
 * Carries the same credentials and `X-BEST-Project` header `request` does,
 * which the plain `?fmt=csv` links cannot (see `entityCsvUrl`) - these exports
 * are for the program the picker shows, not whichever the server last guessed.
 *
 * `Accept` asks for CSV; a failure still arrives as the JSON `{detail}` the
 * rest of the API refuses with, which is what `ensureOk` reads.
 */
async function downloadPost(path: string, body: unknown, filename: string): Promise<void> {
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'text/csv',
      'Content-Type': 'application/json',
      ...(projectSlug ? { 'X-BEST-Project': projectSlug } : {}),
    },
    body: JSON.stringify(body),
  })
  await ensureOk(response)

  await saveBlob(await response.blob(), filename)
}

/** Hand a Blob to the browser as a file called `filename`. */
export async function saveBlob(blob: Blob, filename: string): Promise<void> {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

/**
 * The file name the server chose, from `Content-Disposition`, or `fallback`.
 *
 * The status report names its download after the program, the filter and the
 * format ("example-requirement-status-L2.html"), and a saved file should carry
 * that rather than a name this client made up. The server writes the name
 * between double quotes from a restricted character set, so the quoted form
 * is the only one read.
 */
function serverFilename(response: Response, fallback: string): string {
  const header = response.headers.get('Content-Disposition') ?? ''
  const match = /filename="([^"]+)"/.exec(header)
  return match ? match[1] : fallback
}

/**
 * GET a file and save it under the name the server gives it.
 *
 * The GET twin of `downloadPost`, for the downloads that are a plain address
 * but must still carry what a link cannot: `X-BEST-Project` (so the file is the
 * program the picker shows, not whichever the server last guessed) and the
 * session, with a refusal read the way every other refusal is.
 */
async function downloadGet(path: string, fallbackName: string): Promise<void> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: {
      Accept: '*/*',
      ...(projectSlug ? { 'X-BEST-Project': projectSlug } : {}),
    },
  })
  await ensureOk(response)
  await saveBlob(await response.blob(), serverFilename(response, fallbackName))
}

/** A regenerated .sysml comes back as text, not JSON. */
async function requestText(path: string): Promise<string> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: projectSlug ? { 'X-BEST-Project': projectSlug } : {},
  })
  if (response.status === 401) announceUnauthorized()
  if (!response.ok) throw describe(response.status, null)
  return response.text()
}

function query(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value))
  }
  const text = search.toString()
  return text ? `?${text}` : ''
}

/** The formats of the status report the dashboard offers. */
export type StatusReportFormat = 'html' | 'csv' | 'json'

/**
 * The address of the status report for a format and a set of filters.
 *
 * `fmt` first, then `download=true` for a file, then `level`, `subsystem` and
 * `segment` as comma-separated lists, each left out when empty. Exported so the
 * dashboard's tests can hold the addresses to the contract; the screens call
 * the two methods on `api`.
 */
export function statusReportPath(
  format: StatusReportFormat,
  filters: StatusReportFilters,
  download: boolean,
): string {
  const search = new URLSearchParams({ fmt: format })
  if (download) search.set('download', 'true')
  for (const [key, values] of [
    ['level', filters.levels],
    ['subsystem', filters.subsystems],
    ['segment', filters.segments],
  ] as const) {
    if (values && values.length > 0) search.set(key, values.join(','))
  }
  return `/api/report/status?${search.toString()}`
}

/** The formats and details of a campaign or plan report (issue #119). */
export type VerificationReportFormat = 'html' | 'csv'
export type VerificationReportDetail = 'summary' | 'full'

/**
 * The address of a campaign or plan report, always as a download. Exported so
 * the export menu's test can hold it to the server's contract.
 */
export function verificationReportPath(
  identifier: string,
  format: VerificationReportFormat,
  detail: VerificationReportDetail,
): string {
  const search = new URLSearchParams({ fmt: format, detail, download: 'true' })
  return `/api/verification/${encodeURIComponent(identifier)}/report?${search.toString()}`
}

/**
 * The largest page the API will answer, and therefore the most rows a screen
 * that filters in the browser can be filtering over.
 *
 * Named once, here, because it is a fact about the API and not a preference of
 * any screen: `limit` is declared `Query(500, ge=1, le=5000)` on every listing
 * route in `web/api.py`. Asking for more is a 422, not a bigger page.
 *
 * **Every caller that uses this owes the reader a truncation notice.** Filtering
 * client-side over a capped fetch is only honest while the cap is not reached;
 * past it, narrowing the filter cannot reveal the rows that were never
 * fetched, and a table showing 5,000 of 6,000 looks exactly like a table
 * showing everything. Compare `page.total` against the rows you got and say so.
 *
 * For scale: the largest real program (BALROG) has 2,283 elements and 229
 * requirements, so this is roughly double the whole model today.
 */
export const PAGE_LIMIT = 5000

export const api = {
  /** What BEST can edit. Fetched once; it changes only when the tool does. */
  schema: () => request<Schema>('/api/schema'),

  // ---- programs ----------------------------------------------------------
  //
  // The registry, not the model: these answer before a program is chosen,
  // which is what makes the picker reachable on a fresh install.

  projects: () => request<{ items: ProjectRow[]; current: string | null }>('/api/projects'),

  /** Which program and branch this session would work against. */
  currentProject: () => request<CurrentProject>('/api/projects/current'),

  /** Work in a program from now on. Stored server-side, so it follows you. */
  chooseProject: (slug: string) =>
    request<ProjectRow>('/api/projects/current', {
      method: 'PUT',
      body: JSON.stringify({ slug }),
    }),

  createProject: (body: {
    slug: string
    name?: string
    description?: string
    gitlab_project?: string
    default_branch?: string
  }) => request<ProjectRow>('/api/projects', { method: 'POST', body: JSON.stringify(body) }),

  /**
   * The open issues somebody could start work on.
   *
   * `mine` narrows to what is assigned to the caller; `review` to the issues
   * behind open merge requests that name the caller as a reviewer. They are
   * different questions and the server refuses both together (422), so a caller
   * sends at most one.
   */
  issues: (
    slug: string,
    options: { search?: string; mine?: boolean; review?: boolean } = {},
  ) =>
    request<{ items: IssueRow[] }>(
      `/api/projects/${encodeURIComponent(slug)}/issues${query({
        search: options.search,
        mine: options.mine ? 'true' : undefined,
        review: options.review ? 'true' : undefined,
      })}`,
    ),

  /**
   * Who a piece of work or a merge request can be handed to.
   *
   * The whole list in one request: a project's members are a short list, and the
   * picker filters it in the browser, so there is no `search` here to keep in
   * step with it. Bots and inactive people are never in it.
   */
  members: (slug: string) =>
    request<MembersPayload>(`/api/projects/${encodeURIComponent(slug)}/members`),

  /** One issue by number, for naming what you are working on. */
  issue: (slug: string, iid: number) =>
    request<IssueRow>(`/api/projects/${encodeURIComponent(slug)}/issues/${iid}`),

  /**
   * Begin, or resume, work on an issue.
   *
   * Raises the issue when given a title rather than a number, creates its
   * branch, and imports the model into it so there is something to edit.
   * Resuming leaves an existing working database alone.
   */
  startWork: (
    slug: string,
    body: {
      issue?: number
      title?: string
      description?: string
      labels?: string[]
      /**
       * Who NEW work is assigned to. Omitted or null means the caller; the
       * server ignores it when resuming an existing issue.
       */
      assignee_id?: number | null
    },
  ) =>
    request<WorkItem>(`/api/projects/${encodeURIComponent(slug)}/work`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ---- finishing a piece of work -----------------------------------------
  //
  // The bookend to startWork. All three take the branch from what BEST already
  // remembers for the signed-in person, so no caller passes one.

  /** Has this work been proposed? Has it landed? Read-only, safe to re-ask. */
  mergeState: (slug: string) =>
    request<MergeState>(`/api/projects/${encodeURIComponent(slug)}/merge`),

  /**
   * Propose the current branch for review.
   *
   * Every field is optional and every empty one means "work it out": the
   * title becomes the issue's own, and the description is prepended to the
   * repository's template under a `Closes #N` line.
   *
   * 201 when it opened one and 200 when it found one already open - which is
   * not an error, so the answer arrives the same way either way and `created`
   * on the result says which happened.
   */
  proposeMerge: (
    slug: string,
    body: {
      title?: string
      description?: string
      issue?: number
      /**
       * Omitted leaves an open merge request's reviewers alone; an array, even
       * an empty one, sets them.
       */
      reviewer_ids?: number[] | null
      /**
       * Opt out of asking the owners GitLab names when a NEW merge request is
       * opened. The server defaults to asking; no screen sends this today.
       */
      add_owners?: boolean
    } = {},
  ) =>
    request<MergeRequest>(`/api/projects/${encodeURIComponent(slug)}/merge`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Clear up after the merge: reap the branch database, return to the default.
   *
   * `refresh` re-reads the default branch so it contains what just merged; it
   * is the slow part, and the only reason to turn it off is clearing up
   * several branches in a row.
   */
  finishWork: (slug: string, body: { refresh?: boolean } = {}) =>
    request<FinishResult>(`/api/projects/${encodeURIComponent(slug)}/finish`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Rewrite a program's files in the form BEST regenerates them.
   *
   * Its own commit, because the first write to a hand-authored file reformats
   * the whole thing — a diff worth reviewing once, and worth keeping out of an
   * unrelated change. After it, saves are ordinary line diffs.
   */
  normalize: (slug: string, ref = '') =>
    request<{ ref: string; files: string[]; already_normalized: boolean }>(
      `/api/projects/${encodeURIComponent(slug)}/normalize`,
      { method: 'POST', body: JSON.stringify({ ref }) },
    ),

  /** Branch databases GitLab no longer has a branch for. */
  staleBranches: (slug: string) =>
    request<{ live_branches: string[]; items: Array<{ branch: string; bytes: number }> }>(
      `/api/projects/${encodeURIComponent(slug)}/stale`,
    ),

  /** Delete them. They are caches; the .sysml in the repository is the record. */
  reapStale: (slug: string) =>
    request<{ reaped: string[]; moved: string[] }>(
      `/api/projects/${encodeURIComponent(slug)}/stale`,
      {
        method: 'DELETE',
      },
    ),

  /** The branches a program's model could be read from. */
  branches: (slug: string) =>
    request<{ project: string; repository: string; items: BranchRow[] }>(
      `/api/projects/${encodeURIComponent(slug)}/branches`,
    ),

  /**
   * Read a program's model out of GitLab into its database.
   *
   * Replaces what was there. Nothing is written unless every file parses, so a
   * 422 means the model is exactly as it was.
   */
  importProject: (slug: string, ref = '') =>
    request<ImportResult>(`/api/projects/${encodeURIComponent(slug)}/import`, {
      method: 'POST',
      body: JSON.stringify({ ref }),
    }),

  /** Remember where someone is in a program. Omitted fields are untouched. */
  setProjectState: (
    slug: string,
    state: { hidden?: boolean; branch?: string; issue?: string },
  ) =>
    request<ProjectRow>(`/api/projects/${encodeURIComponent(slug)}`, {
      method: 'PATCH',
      body: JSON.stringify(state),
    }),

  /**
   * Which BEST this is. The one call that needs no sign-in, so the label can
   * be drawn on the sign-in screen too.
   */
  version: () => request<VersionInfo>('/api/version'),

  whoami: () =>
    request<{
      name: string
      role: string
      method: string
      anonymous: boolean
      can_write: boolean
      /** A sandbox has no repository behind it, so its default branch is writable. */
      sandbox?: boolean
      /** The role in the BEST project itself; null for a person in programs only. */
      install_role?: string | null
      /** The program `role` and `can_write` are about, when one is resolved. */
      program?: string | null
      /** The role in that program's own GitLab project. */
      program_role?: string | null
      /**
       * The tag of the baseline this person is viewing, or null. While it is set
       * `can_write` is false: a baseline is a read-only copy of a tag.
       */
      baseline?: string | null
    }>('/api/whoami'),

  /** The values an enumeration field accepts, discovered from the model. */
  choices: (kind: string, field: string) =>
    request<{ choices: string[] }>(`/api/schema/${kind}/${field}/choices`).then((r) => r.choices),

  /**
   * Any further key is passed through as a query parameter, for the narrowing
   * a listing owns itself - `usable_as_type` on parts.
   */
  list: (
    plural: string,
    options: { search?: string; limit?: number; offset?: number; [param: string]: string | number | undefined } = {},
  ) =>
    request<Page<EntityRow>>(`/api/${plural}${query(options)}`),

  get: (plural: string, id: string) =>
    request<EntityRow>(`/api/${plural}/${encodeURIComponent(id)}`),

  update: (plural: string, id: string, changes: Record<string, unknown>) =>
    request<{ kind: string; identifier: string; changed: Record<string, unknown> }>(
      `/api/${plural}/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(changes) },
    ),

  create: (plural: string, body: Record<string, unknown>) =>
    request<{ kind: string; identifier: string; created: boolean }>(`/api/${plural}`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Delete an element: it, what is written inside it, and the links to and
   * from it. Never another element because it was linked to this one.
   *
   * No `cascade`. The API still accepts one, so an old client does not break,
   * and ignores it: there is nothing below an element left to take with it.
   */
  remove: (plural: string, id: string) =>
    request<DeleteResult>(`/api/${plural}/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /**
   * What `remove` would do, without doing it. Read-only, so it needs no
   * write permission - the dialog can say so to someone who cannot delete.
   */
  deleteImpact: (plural: string, id: string) =>
    request<DeleteImpact>(`/api/${plural}/${encodeURIComponent(id)}/delete-impact`),

  /**
   * Give a requirement another source, or take one away.
   *
   * ROUTES OF THEIR OWN, not `update`. A source is a SourceOf connection
   * written inside the requirement, not a field, so a PATCH to
   * /requirements/{id} cannot express one.
   */
  addSource: (identifier: string, source: string) =>
    request<{ derived: string; source: string; created: boolean }>(
      `/api/requirements/${encodeURIComponent(identifier)}/sources`,
      { method: 'POST', body: JSON.stringify({ source }) },
    ),

  removeSource: (identifier: string, source: string) =>
    request<{ derived: string; source: string; removed: boolean }>(
      `/api/requirements/${encodeURIComponent(identifier)}/sources/${encodeURIComponent(source)}`,
      { method: 'DELETE' },
    ),

  /** Record, or remove, that two requirements constrain each other. */
  addRelated: (identifier: string, related: string) =>
    request<{ first: string; second: string; created: boolean }>(
      `/api/requirements/${encodeURIComponent(identifier)}/related`,
      { method: 'POST', body: JSON.stringify({ related }) },
    ),

  removeRelated: (identifier: string, related: string) =>
    request<{ first: string; second: string; removed: boolean }>(
      `/api/requirements/${encodeURIComponent(identifier)}/related/${encodeURIComponent(related)}`,
      { method: 'DELETE' },
    ),

  /** Where a new requirement goes, and what to call it. */
  suggestRequirement: (options: {
    level: number
    segment?: string
    subsystem?: string
  }) => request<Suggestion>(`/api/requirements/suggest${query(options)}`),

  /** An ID for a new test case, plan or campaign (issue #118). */
  suggestVerification: (options: { group: string; package?: string; parent?: string }) =>
    request<Suggestion>(`/api/verification/suggest${query(options)}`),

  /** Packages a new declaration can go in, shallowest first. */
  packages: (search?: string) =>
    request<{ items: PackageRow[] }>(`/api/packages${query({ search })}`).then((r) => r.items),

  /**
   * Packages a new element of one KIND belongs in (issue #114): its base
   * package, narrowed to the level where one is given. `warning` is set when
   * nothing matched and the list fell back to a wider one.
   */
  packagesFor: (kind: string, level?: string | number) =>
    request<{ items: PackageRow[]; expected: string; warning: string }>(
      `/api/packages${query({ kind, level })}`,
    ),

  createPackage: (body: { parent: string; name: string; short_name?: string }) =>
    request<{ name: string; short_name: string | null; qualified_name: string }>(
      '/api/packages',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /**
   * Declare an abstract requirement TYPE.
   *
   * No listing counterpart, deliberately: /api/schema already reports every
   * declared type with the level it reaches, the fields it carries and its
   * template, and that is what a form reads to build itself. A second endpoint
   * answering "what types are there" would be a second answer to maintain.
   */
  createRequirementType: (body: {
    name: string
    identifier?: string | null
    level_type: string
    specializes?: string[]
    template?: string
    attributes?: { name: string; declared_type: string; description: string }[]
  }) =>
    request<{ name: string; identifier: string | null; qualified_name: string }>(
      '/api/requirement-types',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /** Every requirement type declared anywhere in the model. */
  requirementTypes: () =>
    request<{ items: RequirementTypeRow[] }>('/api/requirement-types').then((r) => r.items),

  updateRequirementType: (
    target: string,
    body: {
      template?: string
      attributes?: { name: string; declared_type: string; description: string }[]
    },
  ) =>
    request<{ name: string; changed: Record<string, unknown> }>(
      `/api/requirement-types/${target}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),

  /** Declare attributes on ONE requirement, inside it. All or nothing. */
  declareRequirementAttributes: (
    identifier: string,
    attributes: { name: string; declared_type: string; description: string; values?: string[] }[],
  ) =>
    request<{ identifier: string; declared: { name: string }[] }>(
      `/api/requirements/${encodeURIComponent(identifier)}/attributes`,
      { method: 'POST', body: JSON.stringify({ attributes }) },
    ),

  /** Remove an attribute a requirement declared on itself, and its value. */
  removeRequirementAttribute: (identifier: string, name: string) =>
    request<{ identifier: string; removed: string }>(
      `/api/requirements/${encodeURIComponent(identifier)}/attributes/${encodeURIComponent(name)}`,
      { method: 'DELETE' },
    ),

  deleteRequirementType: (target: string) =>
    request<{ name: string; removed: boolean }>(`/api/requirement-types/${target}`, {
      method: 'DELETE',
    }),

  // ---- read-only views over the same model ---------------------------------

  summary: () => request<Summary>('/api/summary'),

  /**
   * Coverage grouped by one column.
   *
   * The endpoint answers a MAP keyed by the value - {"gnc": {...}, "(unset)":
   * {...}} - which is a fair representation of a group-by but awkward to
   * render. It is flattened to a list here, once, so no screen has to know
   * that the wire shape and the useful shape differ.
   */
  coverage: (by: CoverageAxis): Promise<CoverageGroup[]> =>
    request<{ by: string; groups: Record<string, Omit<CoverageGroup, 'key'>> }>(
      `/api/coverage${query({ by })}`,
    ).then((r) =>
      Object.entries(r.groups ?? {}).map(([key, counts]) => ({ key, ...counts })),
    ),

  findings: () => request<{ total: number; items: Finding[] }>('/api/findings'),

  /**
   * The requirement status report as numbers: GET /api/report/status?fmt=json.
   *
   * Always the customer variant (`detail` is not sent and defaults to summary),
   * which is counts and statuses only. The filters are comma-separated lists
   * and are left out when empty; a filter matching nothing is an empty report,
   * not an error.
   */
  statusReport: (filters: StatusReportFilters = {}) =>
    request<StatusReport>(statusReportPath('json', filters, false)),

  /**
   * Save the status report as a file: the HTML page, or its numbers as CSV or
   * JSON. The file name is the server's. Never the internal variant.
   */
  downloadStatusReport: (format: StatusReportFormat, filters: StatusReportFilters = {}) =>
    downloadGet(statusReportPath(format, filters, true), `requirement-status.${format}`),

  glossary: () => request<GlossaryPage>('/api/glossary'),

  flowdown: () =>
    request<{ items: FlowdownRow[] }>('/api/flowdown').then((r) => r.items),

  design: () => request<{ items: DesignRow[] }>('/api/design').then((r) => r.items),

  /**
   * The same endpoint, kept WHOLE, for the budgets screen.
   *
   * `/api/design` answers three keys and `design()` above throws two away,
   * because the diagram and the parts tree want only the rows. The budget needs
   * `attributes` - which quantities roll up, as the MODEL declares them, and
   * therefore how many charts there are - and `unstorable`, the quantities the
   * model says aggregate that BEST has no column for. Both are read rather than
   * assumed, so a program marking a fourth attribute `@RollsUp` appears on that
   * screen with nothing here being edited.
   *
   * A second method rather than a wider return on the first: changing `design()`
   * would edit every caller for the benefit of one.
   */
  /**
   * `state` resolves BOTH the totals and the budget verdicts in that state,
   * on the server, in one walk. Not two requests to be reconciled here: a
   * total taken in a state and an allowance taken at the baseline would
   * report an overrun wherever a state legitimately draws more than nominal,
   * and there is nothing about the pair of numbers that would look wrong.
   *
   * Omitted, it answers the baseline exactly as it did before the parameter
   * existed. A state identifier the model does not have is a 404 rather than
   * a quiet fall back - the numbers would look like the nominal ones, which
   * is precisely what nobody would catch.
   */
  designBudget: (scope?: { state?: string; worstOf?: string }) => {
    // ONE OR THE OTHER, never both - they are different questions and the
    // server refuses the pair rather than picking one. Built here as a
    // discriminated choice so a caller cannot express the refused case.
    const query = scope?.worstOf
      ? `?worst_of=${encodeURIComponent(scope.worstOf)}`
      : scope?.state
        ? `?state=${encodeURIComponent(scope.state)}`
        : ''
    return request<{
      attributes: RollingAttribute[]
      unstorable: string[]
      items: DesignRow[]
    }>(`/api/design${query}`)
  },

  /**
   * Parts, each saying whether it is a definition and whether it is contained.
   *
   * Its own method rather than api.list('parts') so the two extra columns are
   * typed: a screen that shows definitions only cannot be written against a row
   * that does not admit to being one.
   */
  parts: (
    options: {
      search?: string
      limit?: number
      offset?: number
      /**
       * Only the rows that may legally be the declared type of a usage:
       * a definition, and not abstract.
       *
       * Filtered on the server so no screen has to know what `abstract` means.
       * A picker offering types for a child part must not offer a generic kind
       * like Sensor, which exists to be specialized rather than used.
       */
      usable_as_type?: boolean
    } = {},
  ) =>
    request<PartsPage>(
      `/api/parts${query({
        ...options,
        usable_as_type: options.usable_as_type ? 'true' : undefined,
      })}`,
    ),

  /** One part, with the usages declared inside it and what each is typed by. */
  part: (identifier: string) =>
    request<PartDetail>(`/api/parts/${encodeURIComponent(identifier)}`),

  /**
   * Which attributes a part of this type can carry, from the model.
   *
   * An attribute belongs to the part def that DECLARES it - `powerGeneration`
   * lives on `EchelonPowerSource` - so only a part whose type reaches that def
   * has one. The same rule the rollup gates on, asked of one type: without it a
   * create form offers a structural bracket its chamber pressure.
   */
  /**
   * The quantity kinds a NEW attribute may be declared as.
   *
   * The type picker's options, and the reason the form has no unit field: a
   * quantity's unit is a fact about its kind, so choosing `Power` chooses watts.
   * Served from the same table that units a declared attribute, so the form
   * cannot offer a type the rest of BEST would then refuse to unit.
   */
  /**
   * Add a value to the vocabulary a field chooses from.
   *
   * Behind the picker's "+ Add Option". A program starts with no propellants -
   * the library declares the abstract base and leaves the values to each
   * program - so the first value is what brings the enum into existence.
   */
  addChoice: (identifier: string, field: string, value: string) =>
    request<{ field: string; declared_in: string; choices: string[] }>(
      `/api/parts/${encodeURIComponent(identifier)}/choices/${encodeURIComponent(field)}`,
      { method: 'POST', body: JSON.stringify({ value }) },
    ),

  /**
   * Add a value to a field's vocabulary when no entity is named.
   *
   * The same "+ Add option" as `addChoice`, for a field whose schema says
   * `can_add_choice` - a requirement's `budget`. The list is the program's, not
   * any requirement's, so the create form can add before the row exists, which
   * `addChoice` cannot. The answer has `addChoice`'s shape.
   */
  addSchemaChoice: (kind: string, field: string, value: string) =>
    request<{ field: string; declared_in: string; choices: string[] }>(
      `/api/schema/${encodeURIComponent(kind)}/${encodeURIComponent(field)}/choices`,
      { method: 'POST', body: JSON.stringify({ value }) },
    ),

  attributeTypes: () =>
    request<{ items: AttributeTypeChoice[] }>('/api/attribute-types').then(
      (answer) => answer.items,
    ),

  partAttributes: (identifier: string) =>
    request<{ attributes: string[] }>(
      `/api/parts/${encodeURIComponent(identifier)}/attributes`,
    ).then((answer) => answer.attributes),

  /**
   * The same endpoint, kept WHOLE, for the state-values panel.
   *
   * `partAttributes` above throws away everything but the names, because that
   * is all its caller wants. A second method rather than a wider return on the
   * first: widening it would edit every caller for the benefit of one - the
   * same call `designBudget` makes against `design`.
   */
  partStateValues: (identifier: string) =>
    request<{ attributes: string[]; state_values: Record<string, StateValue[]> }>(
      `/api/parts/${encodeURIComponent(identifier)}/attributes`,
    ).then((answer) => answer.state_values ?? {}),

  /**
   * Replace the state-qualified figures on ONE attribute.
   *
   * PUT and wholesale, like transitions: an `@InState` has no identity of its
   * own - it is keyed by the state it names - so a caller renaming that state
   * is indistinguishable from one replacing the annotation.
   *
   * Its own call rather than part of the attributes save, because it is a
   * different write path with different semantics: the attribute table sends
   * changed fields, this sends a whole list.
   */
  setPartStateValues: (identifier: string, feature: string, values: StateValue[]) =>
    request<{ kind: string; identifier: string; feature: string; state_values: number }>(
      `/api/parts/${encodeURIComponent(identifier)}/attributes/${encodeURIComponent(feature)}/states`,
      { method: 'PUT', body: JSON.stringify({ state_values: values }) },
    ),

  /**
   * Declare more child usages inside a definition that already exists.
   *
   * The other half of `child_parts` at creation: a decomposition is rarely known
   * in full at the moment the definition is made. Takes the SAME rows, so the
   * wizard's table and the add-a-child table are one component.
   *
   * All or nothing, like creation - one transaction, one regeneration check - so
   * a failure leaves nothing behind and the same body may be retried.
   */
  addChildParts: (identifier: string, children: ChildPartRow[]) =>
    request<CreatedPart>(`/api/parts/${encodeURIComponent(identifier)}/children`, {
      method: 'POST',
      body: JSON.stringify({ children }),
    }),

  /**
   * The interfaces a part takes part in (issue #52): those written inside it,
   * joining its usages' ports, and those written elsewhere with an end on one
   * of its own ports. `declared_here` says which.
   */
  partConnections: (identifier: string) =>
    request<{ items: PartConnection[] }>(
      `/api/parts/${encodeURIComponent(identifier)}/connections`,
    ).then((answer) => answer.items),

  /** What an interface inside this part def may join, named as it will be written. */
  partEndpoints: (identifier: string) =>
    request<{ items: ConnectionEndpoint[] }>(
      `/api/parts/${encodeURIComponent(identifier)}/endpoints`,
    ).then((answer) => answer.items),

  /**
   * Join two ports with an interface written inside a part def.
   *
   * The server turns the pair round if the interface def's ends only fit that
   * way, so `ends` on the answer is the order actually written.
   */
  connectPorts: (
    identifier: string,
    body: { first: string; second: string; type_name?: string; identifier?: string; name?: string },
  ) =>
    request<{ owner: string; identifier: string; name: string; ends: string[]; created: boolean }>(
      `/api/parts/${encodeURIComponent(identifier)}/connections`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /** Remove an interface written inside a part def, by identifier, name or qualified name. */
  disconnectPorts: (identifier: string, connection: string) =>
    request<{ owner: string; connection: string; removed: boolean }>(
      `/api/parts/${encodeURIComponent(identifier)}/connections/${encodeURIComponent(connection)}`,
      { method: 'DELETE' },
    ),

  /** Every interface type: the library's three disciplines and the program's own. */
  interfaceTypes: () =>
    request<{ items: InterfaceTypeRow[] }>('/api/interface-types').then((answer) => answer.items),

  /** Declare an interface type in the program's InterfaceTypes package. */
  createInterfaceType: (body: {
    name: string
    specializes: string[]
    identifier?: string | null
    description?: string
    attributes?: ReturnType<typeof import('./components/parts/part-wizard-model').attributePayload>
  }) =>
    request<{ name: string; qualified_name: string; created: boolean }>('/api/interface-types', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Change a type's description, or declare more attributes on it. */
  updateInterfaceType: (
    target: string,
    body: {
      description?: string
      attributes?: ReturnType<typeof import('./components/parts/part-wizard-model').attributePayload>
    },
  ) =>
    request<{ name: string; changed: Record<string, unknown> }>(
      `/api/interface-types/${encodeURIComponent(target)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),

  deleteInterfaceType: (target: string) =>
    request<{ name: string; removed: boolean }>(
      `/api/interface-types/${encodeURIComponent(target)}`,
      { method: 'DELETE' },
    ),

  /** State a message whole: its fields and its rate. */
  createMessage: (body: {
    name: string
    identifier?: string | null
    fields?: ReturnType<typeof import('./components/parts/part-wizard-model').attributePayload>
    publish_rate?: string
    specializes?: string
    statement?: string
  }) =>
    request<{ identifier: string; name: string; qualified_name: string }>('/api/icd/messages', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** State a port def whole - what crosses it - or a connector with its pins. */
  createPortDef: (body: {
    name: string
    identifier?: string | null
    specializes?: string
    features?: FeatureDraft[]
    connector_type?: string
    pins?: string[] | number
    statement?: string
  }) =>
    request<{ identifier: string; name: string; qualified_name: string; pins: string[] }>(
      '/api/icd/port-defs',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /** State an interface def whole: its types, two ends, flows and values. */
  createInterfaceDef: (body: {
    name: string
    identifier?: string | null
    types: string[]
    ends?: { name: string; type_name: string; conjugate: boolean }[]
    flows?: { source: string; target: string }[]
    values?: Record<string, string>
    statement?: string
  }) =>
    request<{ identifier: string; name: string; qualified_name: string }>(
      '/api/icd/interface-defs',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /** A connector's pinout, read across its chain. */
  pinout: (identifier: string) =>
    request<Pinout>(`/api/interfaces/${encodeURIComponent(identifier)}/pinout`),

  /** Say what each pin carries, on this port def. */
  setPinout: (identifier: string, pins: { name: string; function: string }[]) =>
    request<Pinout & { changed: string[] }>(
      `/api/interfaces/${encodeURIComponent(identifier)}/pinout`,
      { method: 'PUT', body: JSON.stringify(pins) },
    ),

  /** What an ICD element declares: direction, keyword, name, type. */
  interfaceFeatures: (identifier: string) =>
    request<{ items: InterfaceFeature[] }>(
      `/api/interfaces/${encodeURIComponent(identifier)}/features`,
    ).then((answer) => answer.items),

  /**
   * Create a part DEFINITION, optionally with a parent and children.
   *
   * A part is always created as a definition; usages exist only as children,
   * which is why this takes `parent_part` and `child_parts` rather than offering
   * a definition-or-usage choice.
   *
   * `specializes` is the `:>` on the definition - what this is a KIND OF - and
   * is deliberately not called `type_name`, which the API refuses here: "what is
   * this a kind of" and "what does it sit inside" are two questions, and one
   * name for both is how they got confused.
   */
  createPart: (body: {
    package: string
    identifier: string
    name?: string
    abstract?: boolean
    specializes?: string
    parent_part?: string
    /** Bare definitions, or `{definition, multiplicity}` - see ChildPartRow. */
    child_parts?: ChildPartRow[]
    statement?: string
    multiplicity?: string
    is_illustrative?: boolean
  }) =>
    request<CreatedPart>('/api/parts', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  files: () => request<{ items: SourceFileRow[] }>('/api/files').then((r) => r.items),

  /** The regenerated .sysml for one stored file - the database as the record. */
  exportFile: (id: number) => requestText(`/api/files/${id}/export`),

  /**
   * The RTM as JSON: the columns the program is held to, and the rows.
   *
   * Goes through `request` rather than a bare fetch so it carries
   * `X-BEST-Project`. A raw fetch reads whichever program the SERVER last
   * resolved for this person, which agrees with the picker right up until it
   * does not - and a traceability matrix quietly showing another program's rows
   * is the worst possible way to find that out.
   */
  rtm: () => request<{ columns: string[]; rows: Record<string, string>[] }>('/api/rtm'),

  rtmCsvUrl: () => '/api/rtm?fmt=csv',

  /**
   * The RTM for just these requirements, saved as `filename`.
   *
   * What "export what the filter shows" is on the requirements screen: the ids
   * are the grid's own visible rows. `null` would mean all of them, but the
   * whole-RTM link already says that more cheaply, so the menu never sends it.
   */
  exportRtm: (ids: string[] | null, filename: string) =>
    downloadPost('/api/rtm/export', { ids }, filename),

  /**
   * The whole element tree, flat rows carrying depth.
   *
   * Everything the model holds, not only the registered kinds: an element BEST
   * does not project still appears, uneditable, because a tree that quietly
   * omits part of the file is worse than one that admits it cannot edit it.
   *
   * The shared library is excluded by default. It is not the program's to edit
   * - which was a real defect, not a hypothetical - so seeing it is opt-in.
   */
  elements: (includeLibrary = false) =>
    request<{ items: ElementRow[] }>(
      `/api/elements${query({ include_library: includeLibrary ? 'true' : undefined })}`,
    ).then((r) => r.items),

  /** The regenerated .sysml for one subtree, by qualified name. */
  exportElement: (qualifiedName: string) =>
    requestText(`/api/elements/${encodeURIComponent(qualifiedName)}/export`),

  /**
   * State new declarations inside a package, from SysML text.
   *
   * The counterpart of `exportElement`: that hands back the .sysml for a
   * subtree, and this takes .sysml back, at the same address.
   *
   * `dry_run` is read-only, and here it is worth more than it looks: the server
   * parses, grafts, projects and REGENERATES before rolling back, so a preview
   * that comes back clean has already proved the model survives the paste.
   */
  pasteSysml: (
    qualifiedName: string,
    body: { text: string; dry_run?: boolean },
  ) =>
    request<SysmlPasteResult>(
      `/api/elements/${encodeURIComponent(qualifiedName)}/sysml`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /**
   * Where a suggestion or bug report would go, and whether it can go there.
   *
   * Asked before the button is drawn. A control that fails on click is worse
   * than one that is not there, and the four reasons it might fail - sandbox,
   * no GitLab, not signed in, read-only token - are all invisible from here.
   */
  feedbackTarget: () => request<FeedbackTarget>('/api/feedback'),

  /**
   * Raise a bug report or a suggestion on BEST's own repository.
   *
   * Filed as the signed-in person with their own GitLab token, so the issue
   * has somebody who can be asked a follow-up question.
   */
  sendFeedback: (body: {
    title: string
    detail?: string
    kind?: string
    page?: string
  }) =>
    request<RaisedIssue>('/api/feedback', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * The commits on a branch.
   *
   * An empty ref means the branch BEST already remembers for this person, the
   * same convention mergeState and finishWork use - so no caller has to know
   * which branch it is on to ask what happened on it.
   *
   * What comes back depends on the branch: a work branch's own commits and the
   * one it was cut from, or a page of the default branch's most recent. `limit`
   * and `page` apply to the second only.
   */
  commits: (
    slug: string,
    options: { ref?: string; limit?: number; page?: number } = {},
  ) =>
    request<CommitHistoryPayload>(
      `/api/projects/${encodeURIComponent(slug)}/commits${query(options)}`,
    ),

  /**
   * What this branch CHANGES in the model, and what that obliges a re-review of.
   *
   * The question `commits` cannot answer. That one is a passthrough to GitLab
   * and says what landed on the branch as file hunks; this compares the branch's
   * model against the target's and says which elements moved and which
   * requirements are linked to one that did.
   *
   * `depth` is how many links out the review walk goes, and the server clamps
   * it - past three hops nearly every requirement is reachable from nearly
   * every other, so a deeper walk returns the model and says nothing.
   *
   * A 409 here means one side has never been imported. That is not a fault and
   * the message names the route that fixes it.
   */
  changes: (slug: string, options: { ref?: string; target?: string; depth?: number } = {}) =>
    request<ModelChanges>(
      `/api/projects/${encodeURIComponent(slug)}/changes${query(options)}`,
    ),

  /**
   * Where a change list's file export lives.
   *
   * The SAME query the screen reads, with a format on it - the shape
   * `/api/rtm?fmt=csv` and every entity listing already use. A separate route
   * would let the file and the screen drift into disagreeing about what
   * changed, which is the one thing this feature must not do.
   *
   * `part` picks the section: one shape per file, because the three sections
   * answer different questions and have genuinely different columns.
   * `fmt=md` ignores it and returns the block that goes into the merge request
   * description, byte for byte.
   */
  changesExportUrl: (
    slug: string,
    options: { fmt: 'csv' | 'md'; part?: string; depth?: number } = { fmt: 'csv' },
  ) =>
    `/api/projects/${encodeURIComponent(slug)}/changes${query(options)}`,

  /**
   * Merge the open merge request for this branch.
   *
   * BEST does not decide whether this is allowed and does not try to: GitLab
   * enforces protected-branch rules, approvals and pipeline requirements on
   * this call, and BEST makes it with the signed-in person's own token. A 409
   * is GitLab saying 'not yet' - conflicts, an unmet approval, a pipeline still
   * running - and reads as an ordinary answer rather than a failure. A 403 is
   * GitLab saying THIS PERSON may not merge into that branch: never a 401,
   * which this client reads as an expired session, and not something signing
   * in again would change.
   *
   * Merging does NOT clear up. The branch's working database is still there and
   * `finishWork` is still the thing that removes it, because taking somebody's
   * working copy away in the same click they merged is not what the button says.
   */
  acceptMerge: (slug: string, body: { ref?: string; when_pipeline_succeeds?: boolean } = {}) =>
    request<MergeResult>(`/api/projects/${encodeURIComponent(slug)}/merge/accept`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Approve the open merge request for this branch, as the signed-in person.
   *
   * GitLab alone decides whether that is allowed (issue #99); BEST adds no
   * reviewer or role check. Send `sha`, the head the person LOOKED AT, so a
   * branch that moved after they looked is refused (409) instead of approved.
   * A 403 is GitLab saying THIS PERSON may not approve - never a 401, which this
   * client reads as an expired session.
   */
  approveMerge: (slug: string, body: { ref?: string; sha?: string } = {}) =>
    request<ApprovalResult>(`/api/projects/${encodeURIComponent(slug)}/merge/approve`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Take back the signed-in person's own approval. Refused as approving is. */
  unapproveMerge: (slug: string, body: { ref?: string } = {}) =>
    request<ApprovalResult>(`/api/projects/${encodeURIComponent(slug)}/merge/unapprove`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * The comment threads on the open merge request (issue #99, phase B).
   *
   * Readable by anyone who can read the program. Nothing open is an ordinary
   * answer (`iid` null, no threads), not an error. `element` (phase C) is any
   * of an element's uid, identifier or qualified name; the server resolves it
   * and answers with only that element's threads.
   */
  listDiscussions: (slug: string, ref = '', element = '') =>
    request<DiscussionList>(
      `/api/projects/${encodeURIComponent(slug)}/merge/discussions${query({ ref, element })}`,
    ),

  /**
   * Start a general comment thread. Any program writer may; GitLab still has
   * the last word, and a refusal is a 403 in its words - never a 401, which this
   * client reads as an expired session.
   */
  startDiscussion: (slug: string, body: { body: string; ref?: string }) =>
    request<{ iid: number }>(`/api/projects/${encodeURIComponent(slug)}/merge/discussions`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Reply in a thread. Refused as starting one is. */
  replyToDiscussion: (slug: string, discussionId: string, body: { body: string; ref?: string }) =>
    request<{ iid: number }>(
      `/api/projects/${encodeURIComponent(slug)}/merge/discussions/${encodeURIComponent(discussionId)}/notes`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /** Resolve a thread, or reopen it with `resolved: false`. */
  resolveDiscussion: (slug: string, discussionId: string, body: { resolved: boolean; ref?: string }) =>
    request<{ iid: number }>(
      `/api/projects/${encodeURIComponent(slug)}/merge/discussions/${encodeURIComponent(discussionId)}/resolve`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /**
   * Comment on one element, anchored to its lines in the merge request's diff
   * where GitLab will take that (issue #99, phase C).
   *
   * `element` is the id the element's page is addressed by (`:plural/:id`). A
   * comment GitLab would not place on a line is posted as an ordinary thread
   * and the answer's `notice` says so. 409 is no open merge request on the
   * branch; a 403 is GitLab's refusal in its words - never a 401.
   */
  postElementComment: (slug: string, body: { element: string; body: string; ref?: string }) =>
    request<ElementCommentResult>(
      `/api/projects/${encodeURIComponent(slug)}/merge/element-comments`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /**
   * The program's baselines, newest first (issue #97, phase C). Readable by
   * anyone who can read the program.
   */
  baselines: (slug: string) =>
    request<BaselineList>(`/api/projects/${encodeURIComponent(slug)}/baselines`),

  /**
   * The CI artifacts of one baseline's tag pipeline. Read on demand, when a
   * baseline is expanded: GitLab is asked per tag, so a long list is not.
   */
  baselineArtifacts: (slug: string, tag: string) =>
    request<BaselineArtifacts>(
      `/api/projects/${encodeURIComponent(slug)}/baselines/${encodeURIComponent(tag)}/artifacts`,
    ),

  /**
   * Cut a baseline: a protected tag on the default branch, named by the server.
   *
   * GitLab's protected tags decide who may; BEST adds no gate (decision 1). A
   * 403 is GitLab's refusal in its own words - never a 401, which this client
   * reads as an expired session.
   *
   * The 409 `main_moved` is NOT an error: the default branch moved since the
   * person's copy was imported and they must choose, so it comes back as
   * `{ moved }` for the caller to ask, then post again with `at`. It is read
   * here because `describe` keeps only the message and the two shas are the
   * point. Every other refusal throws as usual.
   */
  cutBaseline: async (
    slug: string,
    body: { notes?: string; at?: '' | 'imported' | 'current' } = {},
  ): Promise<{ cut: BaselineCut } | { moved: BaselineMainMoved }> => {
    const response = await fetch(`/api/projects/${encodeURIComponent(slug)}/baselines`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(projectSlug ? { 'X-BEST-Project': projectSlug } : {}),
      },
      body: JSON.stringify(body),
    })
    if (response.status === 409) {
      try {
        const answer = (await response.clone().json()) as Partial<BaselineMainMoved> & {
          code?: string
        }
        if (answer.code === 'main_moved') {
          return {
            moved: {
              detail: answer.detail ?? '',
              imported_sha: answer.imported_sha ?? '',
              current_sha: answer.current_sha ?? '',
            },
          }
        }
      } catch {
        // Not JSON: an ordinary refusal, read below.
      }
    }
    await ensureOk(response)
    return { cut: (await response.json()) as BaselineCut }
  },

  /**
   * Start reading a baseline into a read-only copy (issue #97, phase D).
   *
   * Answers a status at once - 202 while it is opening, 200 when it already is
   * ready - because importing a big program is slow: the caller polls
   * `baselineStatus` rather than holding this request open. Asking again for one
   * that is opening or ready is harmless.
   */
  openBaseline: (slug: string, tag: string) =>
    request<BaselineStatus>(
      `/api/projects/${encodeURIComponent(slug)}/baselines/${encodeURIComponent(tag)}/open`,
      { method: 'POST' },
    ),

  /** Where opening a baseline has got to, and - once it is ready - what it was built with. */
  baselineStatus: (slug: string, tag: string) =>
    request<BaselineStatus>(
      `/api/projects/${encodeURIComponent(slug)}/baselines/${encodeURIComponent(tag)}/status`,
    ),

  /**
   * What changed in `head` relative to `base`, either side `main` (the default
   * branch's name) or a baseline tag (issue #97, phase E). The answer is the
   * SAME shape `changes` gives, so the Version Control screen's ChangeList draws it.
   *
   * The 409 `baseline_not_opened` is NOT an error: a baseline side has not been
   * read yet, and the person is offered to open it. It comes back as
   * `{ notOpened }` for that, read here because `describe` keeps only the message
   * and the tag is the point. Every other refusal (404 unknown tag, 409/422 the
   * same side twice) throws as usual, with the server's words.
   */
  compareBaselines: async (
    slug: string,
    sides: { base: string; head: string },
  ): Promise<{ changes: ModelChanges } | { notOpened: BaselineNotOpened }> => {
    const response = await fetch(
      `/api/projects/${encodeURIComponent(slug)}/baselines/compare${query(sides)}`,
      {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          ...(projectSlug ? { 'X-BEST-Project': projectSlug } : {}),
        },
      },
    )
    if (response.status === 409) {
      try {
        const answer = (await response.clone().json()) as Partial<BaselineNotOpened> & {
          code?: string
        }
        if (answer.code === 'baseline_not_opened') {
          return { notOpened: { detail: answer.detail ?? '', tag: answer.tag ?? '' } }
        }
      } catch {
        // Not JSON: an ordinary refusal, read below.
      }
    }
    await ensureOk(response)
    return { changes: (await response.json()) as ModelChanges }
  },

  /**
   * Where a comparison's file export lives: the comparison's own address with a
   * format on it, the way `changesExportUrl` does it for a branch, so the file and
   * the screen cannot disagree. Assumes the server takes `fmt` and `part` on the
   * compare route as it does on `changes`.
   */
  compareBaselinesExportUrl: (
    slug: string,
    options: { base: string; head: string; fmt: 'csv' | 'md'; part?: string },
  ) => `/api/projects/${encodeURIComponent(slug)}/baselines/compare${query(options)}`,

  /**
   * Where one kind's CSV export lives.
   *
   * `fmt=csv` on the listing, the same shape /api/rtm?fmt=csv has - and it
   * answers the WHOLE kind, not the page or the filtered set the same address
   * answers as JSON. An export truncated at the page limit re-imports as a
   * fraction of the model wearing the whole model's name.
   *
   * Not a `request` wrapper: this is an href a browser follows, so the download
   * carries the session cookie and BEST never builds the file in memory twice.
   * That does mean it does NOT carry `X-BEST-Project` the way `rtm()` does - the
   * server falls back to the person's remembered project, which is the same
   * fallback `exportFile` has relied on since it was written.
   */
  entityCsvUrl: (plural: string) => `/api/${encodeURIComponent(plural)}?fmt=csv`,

  /**
   * The same whole-kind file with the extra metadata columns: qualified name,
   * declared type, element ID, and who last changed each one and when.
   *
   * A separate address from `entityCsvUrl` rather than a flag on it, so the
   * plain file's columns - the ones an import reads back - can never change
   * because somebody passed `true`.
   */
  entityRichCsvUrl: (plural: string) => `/api/${encodeURIComponent(plural)}?fmt=csv&rich=true`,

  /**
   * One kind's CSV for just these rows (`ids` are the grid's row ids), saved as
   * `filename`. `rich` adds the metadata columns `entityRichCsvUrl` does.
   */
  exportEntities: (plural: string, ids: string[] | null, rich: boolean, filename: string) =>
    downloadPost(`/api/${encodeURIComponent(plural)}/export`, { ids, rich }, filename),

  /**
   * Apply a CSV to one entity kind.
   *
   * `dry_run` is read-only and is what a preview uses: a row whose ID exists is
   * updated, a new ID is created, and anything else is reported rather than
   * guessed at. One commit for the whole file, not one per row.
   *
   * Requirements reach `/api/requirements/import` through this same helper. The
   * route behind it is hand-written and reads an RTM's own columns, but the
   * request and the report are identical - which is the point, and is why there
   * is no second helper for them.
   */
  importEntities: (
    plural: string,
    body: { csv: string; package?: string; dry_run?: boolean },
  ) =>
    request<CsvImportResult>(`/api/${encodeURIComponent(plural)}/import`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Requirements, searched and paged.
   *
   * `api.facets()` was here and went with issue #35 - it filled the menus of a
   * filter bar that no longer exists, and /api/facets went with it.
   *
   * The faceted filters went the same way: the screen holds every row up to
   * PAGE_LIMIT and the grid's own column filters do the faceting, so passing
   * level/status/segment/subsystem back to the server duplicated a filter the
   * table could already apply.
   *
   * `search` remains because the REFERENCE type-ahead uses it - see api.list.
   */
  requirements: (options: { search?: string; limit?: number; offset?: number } = {}) =>
    request<Page<EntityRow>>(`/api/requirements${query(options)}`),

  /** Verification elements, with verification_kind, is_definition and verifies. */
  verification: (options: { search?: string; limit?: number; offset?: number } = {}) =>
    request<Page<VerificationRow>>(`/api/verification${query(options)}`),

  /** Include an existing case def in a plan, or plan def in a campaign (issue #113). */
  includeVerification: (owner: string, definition: string) =>
    request<{ qualified_name: string; name: string; owner: string; definition: string }>(
      `/api/verification/${encodeURIComponent(owner)}/includes`,
      { method: 'POST', body: JSON.stringify({ definition }) },
    ),

  /** Save a campaign or plan def's report, down to its cases (issue #119). */
  downloadVerificationReport: (
    identifier: string,
    format: VerificationReportFormat,
    detail: VerificationReportDetail,
  ) =>
    downloadGet(
      verificationReportPath(identifier, format, detail),
      `${identifier}-test-report-${detail}.${format}`,
    ),

  /** Remove an inclusion: the usage goes, the def stays. */
  removeInclusion: (owner: string, usage: string) =>
    request<{ removed: string; owner: string }>(
      `/api/verification/${encodeURIComponent(owner)}/includes/${encodeURIComponent(usage)}`,
      { method: 'DELETE' },
    ),

  /**
   * One state, with the transitions declared in its body.
   *
   * Transitions are READ here and nowhere else: a transition has no name to
   * look one up by, so there is no listing of them.
   */
  state: (identifier: string) =>
    request<StateRow>(`/api/states/${encodeURIComponent(identifier)}`),

  /**
   * Put a state into a machine: declare a usage of its def there (decision
   * 030). `machine` is a state def or a composite usage; the answer's
   * `identifier` is the usage's path in its machine, e.g. `Modes::Standby`.
   */
  createStateUsage: (body: {
    machine: string
    definition?: string
    new_definition?: { identifier: string; name?: string; type_name?: string; statement?: string }
    name?: string
    initial?: boolean
  }) =>
    request<StateUsageCreated>('/api/state-usages', { method: 'POST', body: JSON.stringify(body) }),

  /** Every transition, every machine and how they link (burndown Phase 8). Read-only. */
  stateMap: () => request<StateMap>('/api/state-map'),

  /**
   * Put states into one machine as usages of their defs, and point `view` at it
   * (decision 030, B and C). Exactly one of `machine` and `new_machine`.
   */
  groupStates: (body: {
    states: string[]
    view?: string
    machine?: string
    new_machine?: { identifier: string; name?: string }
  }) => request<StateGroupResult>('/api/state-groups', { method: 'POST', body: JSON.stringify(body) }),

  /**
   * A state diagram paired with one machine (decision 030): `machine` names an
   * existing one, or `new_machine` makes one with the view. Exactly one of the two.
   */
  createStateView: (body: {
    package: string
    identifier: string
    name?: string
    statement?: string
    machine?: string
    new_machine?: { identifier: string; name?: string; statement?: string }
  }) =>
    request<{ kind: 'view'; identifier: string; name: string; qualified_name: string; machine: string }>(
      '/api/state-views', { method: 'POST', body: JSON.stringify(body) }),

  /**
   * Replace the transitions a state declares.
   *
   * PUT and WHOLESALE: the ordinary form carries no name, so there is nothing
   * to match an incoming edge against an existing one by, and sending the list
   * is unambiguous. It is why this is not a field on the state's update route.
   * `addTransition` / `changeTransition` / `removeTransition` (issue #50) edit
   * ONE instead, addressed by its declaring state and index.
   */
  setTransitions: (identifier: string, transitions: TransitionEdit[]) =>
    request<{ kind: string; identifier: string; transitions: number }>(
      `/api/states/${encodeURIComponent(identifier)}/transitions`,
      { method: 'PUT', body: JSON.stringify({ transitions }) },
    ),

  /**
   * The requirements linked to one element, and what may link to it (issue #48).
   * A link is written on the requirement - PATCH its field - so this only reads.
   */
  links: (kind: string, identifier: string) =>
    request<LinkReport>(`/api/links/${encodeURIComponent(kind)}/${encodeURIComponent(identifier)}`),

  /**
   * Add ONE transition between two states (issue #50). The server decides which
   * state declares it - the innermost one enclosing both - and answers with it.
   */
  addTransition: (edit: TransitionEdit) =>
    request<{ kind: string; owner: string; index: number }>(
      '/api/transitions', { method: 'POST', body: JSON.stringify(edit) }),

  /**
   * Change ONE transition, named as a read reported it: its declaring state,
   * its index there, and its ends as written - a mismatch means the model
   * changed since, and is refused rather than editing a neighbour.
   */
  changeTransition: (read: TouchingTransition, edit: TransitionEdit) =>
    request<{ kind: string; owner: string; index: number }>(
      `/api/states/${encodeURIComponent(read.owner)}/transitions/${read.index}`,
      {
        method: 'PUT',
        body: JSON.stringify({
          ...edit, from_reference: read.from_reference, to_reference: read.to_reference,
        }),
      }),

  /** Remove ONE transition, named as a read reported it. */
  removeTransition: (read: TouchingTransition) =>
    request<{ kind: string; owner: string; removed: boolean }>(
      `/api/states/${encodeURIComponent(read.owner)}/transitions/${read.index}${query({
        from_reference: read.from_reference, to_reference: read.to_reference,
      })}`,
      { method: 'DELETE' }),

  /** One function with the successions its body declares - the order of its steps. */
  function: (identifier: string) =>
    request<EntityRow & { successions: Succession[] }>(`/api/functions/${encodeURIComponent(identifier)}`),

  /**
   * Replace the successions a function declares. WHOLESALE, as transitions are,
   * and for the same reason: `first a then b;` carries no name.
   */
  setSuccessions: (
    identifier: string,
    successions: { source: string; target: string; identifier?: string; name?: string }[],
  ) =>
    request<{ kind: string; identifier: string; successions: number }>(
      `/api/functions/${encodeURIComponent(identifier)}/successions`,
      { method: 'PUT', body: JSON.stringify({ successions }) },
    ),

  /** One view with its exposes, the states they show, and where each box was placed. */
  view: (identifier: string) =>
    request<ViewDetail>(`/api/views/${encodeURIComponent(identifier)}`),

  /**
   * Replace what a view exposes - adding a box to a diagram, or removing one.
   *
   * WHOLESALE, like transitions: an expose has no name. Each entry is a
   * qualified name, optionally ending `::*` (its members) or `::**` (everything).
   */
  setExposes: (identifier: string, exposes: string[]) =>
    request<{ kind: string; identifier: string; exposes: number }>(
      `/api/views/${encodeURIComponent(identifier)}/exposes`,
      { method: 'PUT', body: JSON.stringify({ exposes }) },
    ),

  /**
   * Replace where a view's boxes are drawn. Commits the layout file beside the
   * model, never the model itself - moving a box is not a model change.
   */
  setLayout: (identifier: string, nodes: Record<string, LayoutBox>) =>
    request<{ path: string; nodes: number; changed: boolean }>(
      `/api/views/${encodeURIComponent(identifier)}/layout`,
      { method: 'PUT', body: JSON.stringify({ nodes }) },
    ),
}
